import { api, apiJson, API_BASE, getToken } from './client.js';

function qs(params = {}) {
  const out = new URLSearchParams();
  for (const [key, value] of Object.entries(params || {})) {
    if (value === undefined || value === null || value === '') continue;
    out.set(key, String(value));
  }
  const text = out.toString();
  return text ? `?${text}` : '';
}

export async function getModelMedia(modelId, params = {}) {
  const pagedMode =
    params.limit !== undefined ||
    params.offset !== undefined ||
    params.source !== undefined ||
    params.folder_id !== undefined ||
    params.folder_name !== undefined;

  if (pagedMode) {
    return apiJson(`/admin/models/${modelId}/media${qs(params)}`, {}, 'Failed to load media');
  }

  const { photo_session_id } = params;
  const PAGE_SIZE = 200;
  let offset = 0;
  let allItems = [];
  let downloadQueueCount = 0;

  while (true) {
    const pageParams = new URLSearchParams({ limit: PAGE_SIZE, offset });
    if (photo_session_id != null) pageParams.set('photo_session_id', photo_session_id);
    const r = await apiJson(`/admin/models/${modelId}/media?${pageParams}`, {}, 'Failed to load media');
    const inner = r?.data ?? r;
    const list = Array.isArray(inner?.list) ? inner.list : [];
    allItems = allItems.concat(list);
    if (typeof inner?.download_queue_count === 'number') {
      downloadQueueCount = inner.download_queue_count;
    }
    if (!inner?.hasMore) break;
    offset += PAGE_SIZE;
  }

  return {
    data: {
      list: allItems,
      hasMore: false,
      total: allItems.length,
      download_queue_count: downloadQueueCount,
    },
  };
}

export async function getModelMediaFolders(modelId) {
  return apiJson(`/admin/models/${modelId}/media/folders`, {}, 'Failed to load media folders');
}

const BODY_PARTS_VALUES = ['face', 'chest', 'butt', 'pussy', 'legs'];
const CONTENT_CATEGORY_VALUES = ['selfie', 'everyday_photo', 'teaser', 'explicit', 'voice_phrase'];

export async function updateMedia(
  modelId,
  mediaId,
  { is_paid, price_min, price_max, hotness, body_parts, content_category, excluded_from_auto_use, description },
) {
  const body = {};
  if (is_paid !== undefined) body.is_paid = !!is_paid;
  if (price_min !== undefined) {
    body.price_min = price_min == null || price_min === '' ? null : Number(price_min);
  }
  if (price_max !== undefined) {
    body.price_max = price_max == null || price_max === '' ? null : Number(price_max);
  }
  if (hotness !== undefined) {
    body.hotness =
      hotness == null || hotness === ''
        ? null
        : Number(hotness) >= 1 && Number(hotness) <= 10
          ? Number(hotness)
          : null;
  }
  if (body_parts !== undefined) {
    body.body_parts = Array.isArray(body_parts)
      ? body_parts.filter((x) => typeof x === 'string' && BODY_PARTS_VALUES.includes(x))
      : null;
    if (body.body_parts?.length === 0) body.body_parts = null;
  }
  if (content_category !== undefined) {
    body.content_category =
      content_category && CONTENT_CATEGORY_VALUES.includes(content_category)
        ? content_category
        : null;
  }
  if (excluded_from_auto_use !== undefined) body.excluded_from_auto_use = !!excluded_from_auto_use;
  if (description !== undefined) {
    body.description = description == null || description === '' ? null : String(description).trim() || null;
  }
  return apiJson(
    `/admin/models/${modelId}/media/${mediaId}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    'Failed to update media',
  );
}

export async function hideMedia(modelId, mediaId) {
  return apiJson(
    `/admin/models/${modelId}/media/${mediaId}/hide`,
    { method: 'POST' },
    'Failed to hide media',
  );
}

export async function restoreMedia(modelId, mediaId) {
  return apiJson(
    `/admin/models/${modelId}/media/${mediaId}/restore`,
    { method: 'POST' },
    'Failed to restore media',
  );
}

export async function uploadAudioPhrase(modelId, file, { title, phrase_text, voice_id } = {}) {
  const formData = new FormData();
  formData.append('file', file);
  if (title) formData.append('title', title);
  if (phrase_text) formData.append('phrase_text', phrase_text);
  if (voice_id) formData.append('voice_id', voice_id);
  return apiJson(
    `/admin/models/${modelId}/media/audio-phrases`,
    { method: 'POST', body: formData, skipContentType: true },
    'Failed to upload audio phrase',
  );
}

export async function uploadLocalMedia(modelId, files, { description, hotness, is_paid } = {}) {
  const formData = new FormData();
  const list = Array.from(files || []);
  list.forEach((file) => formData.append('files', file));
  if (description) formData.append('description', description);
  if (hotness !== undefined && hotness !== null && hotness !== '') formData.append('hotness', hotness);
  if (is_paid !== undefined) formData.append('is_paid', is_paid ? 'true' : 'false');
  return apiJson(
    `/admin/models/${modelId}/media/upload`,
    { method: 'POST', body: formData, skipContentType: true },
    'Failed to upload media',
  );
}

export async function syncModelMedia(modelId, downloadFullMedia = false, params = {}) {
  return apiJson(
    `/admin/models/${modelId}/media/sync${qs({ ...params, download_full_media: downloadFullMedia ? 'true' : undefined })}`,
    { method: 'POST', body: JSON.stringify({}) },
    'Failed to start media sync',
  );
}

export async function downloadAllFullMedia(modelId, params = {}) {
  return apiJson(
    `/admin/models/${modelId}/media/download-all${qs(params)}`,
    { method: 'POST', body: JSON.stringify({}) },
    'Failed to queue media download',
  );
}

export async function describeMediaPhotos(modelId, params = {}) {
  const body = {};
  if (params.media_ids !== undefined) body.media_ids = params.media_ids;
  if (params.folder_id !== undefined) body.folder_id = params.folder_id;
  if (params.folder_name !== undefined) body.folder_name = params.folder_name;
  if (params.source !== undefined) body.source = params.source;
  if (params.limit !== undefined) body.limit = Number(params.limit) || 50;
  if (params.overwrite !== undefined) body.overwrite = !!params.overwrite;
  return apiJson(
    `/admin/models/${modelId}/media/describe`,
    { method: 'POST', body: JSON.stringify(body) },
    'Failed to queue photo descriptions',
  );
}

export async function downloadMediaFull(modelId, mediaId) {
  return apiJson(
    `/admin/models/${modelId}/media/${mediaId}/download`,
    { method: 'POST', body: JSON.stringify({}) },
    'Failed to queue media download',
  );
}

export async function cancelSyncModelMedia(modelId) {
  return apiJson(
    `/admin/models/${modelId}/media/sync/cancel`,
    { method: 'POST', body: JSON.stringify({}) },
    'Failed to stop media sync',
  );
}

export function getMediaFileUrl(modelId, mediaId, kind = 'preview') {
  return `/admin/models/${modelId}/media/file/${mediaId}?kind=${kind}`;
}

const BLOB_CACHE_MAX = 300;
const MEDIA_PREVIEW_RETRY_DELAYS_MS = [250, 750];
const FULL_MEDIA_FETCH_CONCURRENCY = 3;
const blobCache = new Map();
const fullMediaFetchQueue = [];
let activeFullMediaFetches = 0;

function withFullMediaFetchSlot(fetchFn) {
  return new Promise((resolve, reject) => {
    const run = () => {
      activeFullMediaFetches += 1;
      Promise.resolve()
        .then(fetchFn)
        .then(resolve, reject)
        .finally(() => {
          activeFullMediaFetches -= 1;
          fullMediaFetchQueue.shift()?.();
        });
    };

    if (activeFullMediaFetches < FULL_MEDIA_FETCH_CONCURRENCY) run();
    else fullMediaFetchQueue.push(run);
  });
}

function getCachedBlobOrFetch(url, fetchFn) {
  const cached = blobCache.get(url);
  if (cached) return Promise.resolve(cached);
  return fetchFn().then((blobUrl) => {
    if (blobUrl && blobCache.size >= BLOB_CACHE_MAX) {
      const [oldUrl, oldBlob] = blobCache.entries().next().value;
      blobCache.delete(oldUrl);
      URL.revokeObjectURL(oldBlob);
    }
    if (blobUrl) blobCache.set(url, blobUrl);
    return blobUrl;
  });
}

export async function fetchMediaFileBlob(modelId, mediaId, kind = 'thumb') {
  const url = getMediaFileUrl(modelId, mediaId, kind);
  return getCachedBlobOrFetch(url, async () => {
    const headers = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const retryDelays = kind === 'preview' || kind === 'thumb' ? [0, ...MEDIA_PREVIEW_RETRY_DELAYS_MS] : [0];

    for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
      if (retryDelays[attempt]) {
        await new Promise((resolve) => setTimeout(resolve, retryDelays[attempt]));
      }
      try {
        const loadFile = async () => {
          const res = await fetch(`${API_BASE}${url}`, { headers, cache: kind === 'full' ? 'no-store' : 'force-cache' });
          const blobUrl = res.ok ? URL.createObjectURL(await res.blob()) : null;
          return { res, blobUrl };
        };
        const { res, blobUrl } = kind === 'full'
          ? await withFullMediaFetchSlot(loadFile)
          : await loadFile();
        if (blobUrl) return blobUrl;
        const retryable = res.status === 429 || res.status >= 500;
        if (!retryable) return null;
      } catch {
        // Preview requests get two short retries; full-size media remains single-shot.
      }
    }
    return null;
  });
}

export async function getModelVaultMedia(modelId, { limit = 24, offset = 0, field = 'recent', type } = {}) {
  const params = new URLSearchParams({ limit, offset, field });
  if (type) params.set('type_', type);
  return apiJson(`/admin/models/${modelId}/media/vault?${params}`, {}, 'Failed to load vault media');
}

export function getMediaProxyUrl(modelId, cdnUrl, stream = false) {
  if (!cdnUrl) return null;
  const encoded = btoa(unescape(encodeURIComponent(cdnUrl)));
  let path = `/admin/models/${modelId}/media/proxy?url=${encodeURIComponent(encoded)}`;
  if (stream) path += '&stream=1';
  return path;
}

export async function fetchMediaProxyBlob(modelId, cdnUrl) {
  const url = getMediaProxyUrl(modelId, cdnUrl);
  if (!url) return null;
  return getCachedBlobOrFetch(url, async () => {
    const res = await api(url, { headers: {} });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  });
}

export async function getMediaProxyToken(modelId, cdnUrl) {
  if (!cdnUrl) return null;
  const encoded = btoa(unescape(encodeURIComponent(cdnUrl)));
  const r = await apiJson(
    `/admin/models/${modelId}/media/proxy-token`,
    { method: 'POST', body: JSON.stringify({ url: encoded }) },
    'Failed to get media proxy token',
  );
  return r?.token ?? null;
}

export function getMediaProxyUrlWithToken(modelId, cdnUrl, token) {
  const path = getMediaProxyUrl(modelId, cdnUrl, true);
  if (!path || !token) return null;
  const sep = path.includes('?') ? '&' : '?';
  return `${API_BASE}${path}${sep}token=${encodeURIComponent(token)}`;
}
